import { useEffect, useRef } from "react";
import { MULTIPLEX_DEBUG, RENDER_DEBUG } from "@/lib/debug-flags";
import { csMark } from "@/lib/channel-switch-profile";
import type { RefObject } from "react";

interface ScrollStateProvider {
  getScrollState?: () =>
    | { scrollTop: number; scrollHeight: number }
    | null
    | undefined;
}

/**
 * Render + multiplex diagnostics for `MessageArea`.
 *
 * - Bumps a per-instance render counter and logs render reason when
 *   `RENDER_DEBUG` is on.
 * - Logs MOUNT / UNMOUNT once per instance, plus an "ACTIVE / HIDDEN"
 *   transition log + a post-rAF follow-up read so the multiplex
 *   behaviour can be verified end-to-end.
 *
 * Both code paths are pure no-ops when their respective flag is off,
 * so this hook stays cheap in production.
 */
export function useRenderDiagnostics(
  channelId: string | null,
  hidden: boolean,
  listRef: RefObject<ScrollStateProvider | null>,
) {
  const parentRenderCountRef = useRef(0);
  parentRenderCountRef.current += 1;
  if (RENDER_DEBUG)
    console.debug(
      `[MessageArea] render #${parentRenderCountRef.current}`,
      `ch=${channelId?.slice(-6)}`,
      hidden ? "HIDDEN" : "ACTIVE",
    );

  // Mount/unmount logging — proves the multiplex is working. When you
  // return to a previously-visited channel, you should see "became ACTIVE"
  // with NO preceding "MOUNT" — the instance was kept alive under
  // display:none and just toggled visible. Gated on MULTIPLEX_DEBUG so you
  // can verify multiplex without the full render-debug firehose.
  useEffect(() => {
    if (MULTIPLEX_DEBUG)
      console.debug(`[mx] MOUNT ch=${channelId?.slice(-6) ?? "-"}`);
    return () => {
      if (MULTIPLEX_DEBUG)
        console.debug(`[mx] UNMOUNT ch=${channelId?.slice(-6) ?? "-"}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (MULTIPLEX_DEBUG) {
      const state = listRef.current?.getScrollState?.();
      console.debug(
        `[mx] became ${hidden ? "HIDDEN" : "ACTIVE"} ch=${channelId?.slice(-6) ?? "-"} scrollTop=${state?.scrollTop ?? "?"} scrollHeight=${state?.scrollHeight ?? "?"}`,
      );
    }
    // Schedule a post-commit follow-up read so we can see what the DOM
    // settled to after browser applied the display change + any RO
    // fires. Also the natural "paint" mark for the channel-switch
    // profile — the channel just became visible and the browser has
    // rendered one frame for it.
    const raf = requestAnimationFrame(() => {
      if (MULTIPLEX_DEBUG) {
        const state2 = listRef.current?.getScrollState?.();
        console.debug(
          `[mx] post-rAF ${hidden ? "HIDDEN" : "ACTIVE"} ch=${channelId?.slice(-6) ?? "-"} scrollTop=${state2?.scrollTop ?? "?"} scrollHeight=${state2?.scrollHeight ?? "?"}`,
        );
      }
      if (!hidden) csMark("paint", channelId);
    });
    return () => cancelAnimationFrame(raf);
  }, [hidden, channelId, listRef]);
}
